import type { GtmBrain, GtmBrainDoc, GtmPlay } from "../api/GtmApi";
import { listPersonas } from "../brain/personas";
import { signalBody } from "../brain/signalsProfile";

export type PlaySuggestionCategory = "buyer_profiles" | "buying_signals";

export type PlaySuggestionKind =
  | "matching_icp"
  | "matching_persona"
  | "hiring_role"
  | "using_technology";

export type PlaySuggestion = {
  id: string;
  category: PlaySuggestionCategory;
  kind: PlaySuggestionKind;
  /** Purple tag line in the card header */
  tag: string;
  /** Audience text used to create the play */
  audience: string;
  /** Optional short name for create */
  name?: string;
};

const BUYER_TAG_ICP = "Matching your ideal customer";
const BUYER_TAG_PERSONA = "Matching your ideal customer";
const SIGNAL_TAG_HIRING = "Hiring for a role";
const SIGNAL_TAG_TECH = "Using a technology";

function hasText(value?: string | null): boolean {
  return !!value && value.trim().length > 0;
}

function firstNonEmpty(...values: Array<string | null | undefined>): string {
  for (const v of values) {
    if (hasText(v)) return (v || "").trim();
  }
  return "";
}

/** Split free-text signals into discrete phrases. */
export function splitSignalPhrases(raw: string, limit = 4): string[] {
  const text = (raw || "").trim();
  if (!text) return [];
  const parts = text
    .split(/\n|;|•|\u2022|(?:,\s+(?=[A-Z]))|(?:(?<=\.)\s+)/)
    .map((p) => p.replace(/^[-*•\d.)\s]+/, "").trim())
    .filter((p) => p.length >= 2 && p.length <= 80);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of parts) {
    const key = p.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
    if (out.length >= limit) break;
  }
  if (out.length === 0 && text.length <= 80) return [text];
  if (out.length === 0) return [text.slice(0, 77) + "…"];
  return out;
}

function normalizeAudienceKey(audience: string): string {
  return audience.trim().toLowerCase().replace(/\s+/g, " ");
}

function alreadyHasPlay(plays: GtmPlay[], audience: string): boolean {
  const key = normalizeAudienceKey(audience);
  return plays.some(
    (p) =>
      normalizeAudienceKey(p.audience) === key ||
      normalizeAudienceKey(p.name) === key
  );
}

function icpAudience(brain: GtmBrain, plays: GtmPlay[]): string | null {
  const roles = (brain.buyingRoles || []).map((r) => r.trim()).filter(Boolean);
  const verticals = (brain.verticals || brain.industries || [])
    .map((v) => v.trim())
    .filter(Boolean);
  const size = (brain.targetCompanySize || "").trim();
  const rolePart = roles.length ? roles.slice(0, 2).join(" and ") : "";
  const verticalPart = verticals.length
    ? `at ${verticals.slice(0, 2).join(" / ")} companies`
    : "";
  const sizePart = size ? `(${size})` : "";

  let audience = "";
  if (rolePart && verticalPart) {
    audience = `${rolePart} ${verticalPart}${sizePart ? ` ${sizePart}` : ""}`;
  } else if (hasText(brain.icpSummary)) {
    audience = brain.icpSummary!.trim();
  } else if (rolePart) {
    audience = size ? `${rolePart} at companies ${size}` : rolePart;
  } else {
    return null;
  }

  const relatedPlay = plays.find((p) => hasText(p.name));
  if (relatedPlay && !audience.toLowerCase().includes("like my play")) {
    audience = `${audience.replace(/\.$/, "")}, like my Play "${relatedPlay.name}"`;
  }
  return audience;
}

function personaAudience(
  personaName: string,
  companySize?: string,
  withSize = false
): string {
  if (withSize && hasText(companySize)) {
    return `People who match my persona "${personaName}" at companies ${companySize!.trim()}`;
  }
  return `People who match my persona "${personaName}"`;
}

function hiringAudience(rolePhrase: string): string {
  const cleaned = rolePhrase
    .replace(/^(hiring|hire|looking for)\s+/i, "")
    .replace(/\.$/, "")
    .trim();
  if (/^decision-makers/i.test(cleaned)) return cleaned;
  return `Decision-makers at companies hiring ${cleaned}`;
}

function techAudience(techPhrase: string): string {
  const cleaned = techPhrase
    .replace(/^(using|uses|use of)\s+/i, "")
    .replace(/\.$/, "")
    .trim();
  if (/^decision-makers/i.test(cleaned)) return cleaned;
  return `Decision-makers at companies using ${cleaned}`;
}

export function buildPlaySuggestions(input: {
  brain: GtmBrain | null | undefined;
  docs: GtmBrainDoc[];
  plays?: GtmPlay[];
  maxBuyer?: number;
  maxSignals?: number;
}): PlaySuggestion[] {
  const brain = input.brain;
  if (!brain) return [];
  const plays = input.plays || [];
  const maxBuyer = input.maxBuyer ?? 6;
  const maxSignals = input.maxSignals ?? 6;
  const out: PlaySuggestion[] = [];
  const used = new Set<string>();

  const push = (s: PlaySuggestion) => {
    const key = normalizeAudienceKey(s.audience);
    if (!key || used.has(key) || alreadyHasPlay(plays, s.audience)) return;
    used.add(key);
    out.push(s);
  };

  // --- Buyer profiles ---
  const icp = icpAudience(brain, plays);
  if (icp) {
    push({
      id: "buyer-icp",
      category: "buyer_profiles",
      kind: "matching_icp",
      tag: BUYER_TAG_ICP,
      audience: icp,
      name: firstNonEmpty(
        (brain.buyingRoles || [])[0],
        (brain.verticals || [])[0],
        "Ideal customer play"
      ),
    });
  }

  const personas = listPersonas(input.docs).filter((p) => hasText(p.doc.name));
  const companySize = brain.targetCompanySize || "";
  let buyerCount = out.filter((s) => s.category === "buyer_profiles").length;

  for (const persona of personas) {
    if (buyerCount >= maxBuyer) break;
    const name = persona.doc.name!.trim();
    push({
      id: `buyer-persona-${persona.key}`,
      category: "buyer_profiles",
      kind: "matching_persona",
      tag: BUYER_TAG_PERSONA,
      audience: personaAudience(name),
      name,
    });
    buyerCount = out.filter((s) => s.category === "buyer_profiles").length;
  }

  // One size-qualified persona card when size is known
  if (buyerCount < maxBuyer && hasText(companySize) && personas[0]?.doc.name) {
    const name = personas[0].doc.name!.trim();
    push({
      id: `buyer-persona-size-${personas[0].key}`,
      category: "buyer_profiles",
      kind: "matching_persona",
      tag: BUYER_TAG_PERSONA,
      audience: personaAudience(name, companySize, true),
      name: `${name} (${companySize.trim()})`,
    });
  }

  // --- Buying signals ---
  const hiringPhrases = splitSignalPhrases(signalBody(brain, "hiring"), 4);
  for (const [i, phrase] of hiringPhrases.entries()) {
    if (out.filter((s) => s.category === "buying_signals").length >= maxSignals) break;
    const audience = hiringAudience(phrase);
    push({
      id: `signal-hiring-${i}`,
      category: "buying_signals",
      kind: "hiring_role",
      tag: SIGNAL_TAG_HIRING,
      audience,
      name: phrase.length > 48 ? `${phrase.slice(0, 45)}…` : phrase,
    });
  }

  const stackPhrases = splitSignalPhrases(signalBody(brain, "stack"), 4);
  for (const [i, phrase] of stackPhrases.entries()) {
    if (out.filter((s) => s.category === "buying_signals").length >= maxSignals) break;
    const audience = techAudience(phrase);
    push({
      id: `signal-stack-${i}`,
      category: "buying_signals",
      kind: "using_technology",
      tag: SIGNAL_TAG_TECH,
      audience,
      name: phrase.length > 48 ? `${phrase.slice(0, 45)}…` : phrase,
    });
  }

  return out;
}

export function groupPlaySuggestions(suggestions: PlaySuggestion[]): {
  buyerProfiles: PlaySuggestion[];
  buyingSignals: PlaySuggestion[];
} {
  return {
    buyerProfiles: suggestions.filter((s) => s.category === "buyer_profiles"),
    buyingSignals: suggestions.filter((s) => s.category === "buying_signals"),
  };
}
